/**
 * Phase F.6 Batch C — Rentals UI.
 *
 * Single tab embedded in InventoryAdmin showing:
 *  - All rental-type inventory items
 *  - For the selected item: an availability checker + the list of reservations
 *  - Quick "New Reservation" dialog
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CalendarClock, CalendarPlus, Trash2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : "—";
const STATUS_COLORS = {
  reserved: "default",
  out: "secondary",
  returned: "outline",
  cancelled: "outline",
  overdue: "destructive",
};

export default function InventoryRentalsTab() {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    quantity: 1,
    start_date: "",
    end_date: "",
    notes: "",
  });
  const [availability, setAvailability] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inventory/rentals/items`);
      setItems(data.items || []);
      if (!selectedItem && data.items?.length) setSelectedItem(data.items[0]);
    } catch (e) {
      toast.error("Failed to load rental items");
    } finally {
      setLoading(false);
    }
  };

  const loadReservations = async () => {
    if (!selectedItem) { setReservations([]); return; }
    try {
      const { data } = await axios.get(`${API}/inventory/rentals/reservations?item_id=${selectedItem.id}`);
      setReservations(data.reservations || []);
    } catch (e) {
      // empty
    }
  };

  useEffect(() => { loadItems(); }, []);
  useEffect(() => { loadReservations(); }, [selectedItem?.id]);

  const checkAvailability = async () => {
    if (!selectedItem || !form.start_date || !form.end_date) return;
    try {
      const { data } = await axios.get(`${API}/inventory/rentals/availability`, {
        params: { item_id: selectedItem.id, start_date: form.start_date, end_date: form.end_date },
      });
      setAvailability(data);
    } catch (e) {
      setAvailability(null);
    }
  };

  useEffect(() => { checkAvailability(); /* eslint-disable-next-line */ }, [form.start_date, form.end_date]);

  const submit = async () => {
    if (!selectedItem) return;
    try {
      await axios.post(`${API}/inventory/rentals/reservations`, {
        item_id: selectedItem.id,
        quantity: Number(form.quantity) || 1,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        notes: form.notes || null,
      });
      toast.success("Reservation created");
      setDialogOpen(false);
      setForm({ quantity: 1, start_date: "", end_date: "", notes: "" });
      loadReservations();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to create reservation");
      toast.error(msg);
    }
  };

  const updateStatus = async (resv, newStatus) => {
    try {
      await axios.patch(`${API}/inventory/rentals/reservations/${resv.id}`, { status: newStatus });
      toast.success(`Marked ${newStatus}`);
      loadReservations();
    } catch (e) { toast.error("Failed to update"); }
  };

  const cancel = async (resv) => {
    try {
      await axios.delete(`${API}/inventory/rentals/reservations/${resv.id}`);
      toast.success("Cancelled");
      loadReservations();
    } catch (e) { toast.error("Failed to cancel"); }
  };

  if (loading) {
    return <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Loading rentals…</CardContent></Card>;
  }

  if (!items.length) {
    return (
      <Card data-testid="rentals-empty">
        <CardContent className="py-10 text-center space-y-2">
          <CalendarClock className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No rental items yet.</p>
          <p className="text-xs text-muted-foreground">Create an inventory item with type <strong>rental</strong> from the Inventory tab to manage reservations here.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="rentals-tab">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Rental Reservations</CardTitle>
            <CardDescription>Book, return, and track rental inventory. Availability is computed live from overlapping reservations.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadReservations}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="new-reservation-btn" disabled={!selectedItem}>
              <CalendarPlus className="h-4 w-4 mr-1" /> New Reservation
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Rental item</Label>
            <Select value={selectedItem?.id || ""} onValueChange={(v) => setSelectedItem(items.find(it => it.id === v))}>
              <SelectTrigger data-testid="rental-item-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {items.map(it => (
                  <SelectItem key={it.id} value={it.id}>
                    {it.name} {it.sku ? `(${it.sku})` : ""} — {it.quantity_on_hand} on hand · ${it.unit_price}/{it.rental_period}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedItem && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedItem.rental_terms || "No rental terms set."}
                {selectedItem.rental_deposit > 0 && <span className="ml-2">Deposit: ${selectedItem.rental_deposit}/unit</span>}
              </p>
            )}
          </div>

          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Deposit</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-40 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reservations.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-6">No reservations for this item.</TableCell></TableRow>
                ) : reservations.map(r => (
                  <TableRow key={r.id} data-testid={`reservation-row-${r.id}`}>
                    <TableCell><Badge variant={STATUS_COLORS[r.status] || "outline"}>{r.status}</Badge></TableCell>
                    <TableCell>{r.quantity}</TableCell>
                    <TableCell>{fmtDate(r.start_date)}</TableCell>
                    <TableCell>{fmtDate(r.end_date)}</TableCell>
                    <TableCell>${r.daily_rate}</TableCell>
                    <TableCell>${r.deposit_collected}</TableCell>
                    <TableCell className="max-w-[180px] truncate" title={r.notes}>{r.notes || "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {r.status === "reserved" && (
                        <Button variant="ghost" size="sm" onClick={() => updateStatus(r, "out")} data-testid={`mark-out-${r.id}`}>Out</Button>
                      )}
                      {r.status === "out" && (
                        <Button variant="ghost" size="sm" onClick={() => updateStatus(r, "returned")} data-testid={`mark-returned-${r.id}`}>Returned</Button>
                      )}
                      {(r.status === "reserved" || r.status === "out") && (
                        <Button variant="ghost" size="sm" onClick={() => cancel(r)} data-testid={`cancel-resv-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="new-reservation-dialog">
          <DialogHeader>
            <DialogTitle>New Reservation</DialogTitle>
            <DialogDescription>
              {selectedItem ? `Reserving "${selectedItem.name}" — ${selectedItem.quantity_on_hand} units total.` : "Pick an item first."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input type="number" min="1" step="1" value={form.quantity} onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))} data-testid="resv-qty" />
              </div>
              <div>
                <Label className="text-xs">Start</Label>
                <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))} data-testid="resv-start" />
              </div>
              <div>
                <Label className="text-xs">End</Label>
                <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))} data-testid="resv-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} />
            </div>
            {availability && (
              <div className={`rounded-md p-2 text-xs ${availability.available_qty >= Number(form.quantity || 1) ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-red-500/10 text-red-700 dark:text-red-300"}`}>
                Available in this window: <strong>{availability.available_qty}</strong> of {availability.total_qty}
                {availability.committed_qty > 0 && <span className="ml-2 opacity-70">({availability.committed_qty} already committed)</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.start_date || !form.end_date} data-testid="resv-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
